<template>
	<div v-if="hypermarketDetails">
		<slot name="header" />
		<div v-if="pending">
			<div v-for="index in 5" :key="index" class="skeleton-gradient h-5 w-full mb-2 rounded-lg" />
			<div class="skeleton-gradient h-8 w-36 mt-3 rounded-xl" />
		</div>
		<template v-else>
			<div
				v-if="selectedBranchLabelFresh && selectedHypermarketInstance"
				aria-labelledby="widget-address-heading"
				:class="'flex flex-col gap-4 py-2'"
			>
				<h2 id="widget-address-heading" class="sr-only">
					Další informace o pobočce
				</h2>
				<address class="flex flex-wrap gap-4 items-center justify-between">
					<h3 class="sr-only">
						Adresa pobočky
					</h3>
					<div class="flex gap-2 not-italic">
						<NuxtIcon
							name="map-pin"
							class="text-base text-icon-primary sm:pt-[.1875rem]"
							filled
							aria-hidden="true"
						/>
						<span>
							{{ selectedHypermarketInstance.content.address }}
						</span>
					</div>
					<StoryblokComponent
						v-for="item in selectedHypermarketInstance.content.mapUrl"
						:key="item.id"
						:blok="item"
						class="btn btn-xs btn-secondary not-italic"
						has-icon-name="map"
						has-icon-class="text-base"
						has-icon-left
					/>
				</address>
			</div>
			<div v-if="selectedHypermarketInstance && selectedHypermarketInstance?.content?.phone" class="flex gap-x-2 items-center py-2">
				<NuxtIcon
					name="phone"
					class="text-sm sm:text-base text-icon-primary -scale-x-100"
					filled
				/>
				<span>Bezplatná infolinka</span>
				<span class="ml-auto flex flex-wrap gap-2">
					<a
						:href="getPhoneLink(selectedHypermarketInstance.content.phone)"
						class="font-bold underline text-body-link hover:decoration-transparent"
						:aria-label="`Zavolat na bezplatnou infolinku ${selectedHypermarketInstance.content.phone}`"
						v-text="selectedHypermarketInstance.content.phone"
					/>
				</span>
			</div>
			<div v-if="capacity !== null" class="flex gap-x-2 items-center py-2 text-body-s">
				<NuxtIcon
					name="users"
					class="text-sm sm:text-base text-icon-primary"
					filled
				/>
				<span>Aktuální obsazenost prodejny</span>
				<span class="ml-auto">{{ n(capacity, {style: 'percent'}) }}</span>
			</div>

			<AbsoluteInternalLink
				v-if="!selectedBranchLabelFresh"
				:to="branchSpecificPath({name: ''})"
				class="btn btn-secondary xl:btn-secondary btn-xs justify-center text-center max-xs:grow mt-1.5 xl:shadow-sm"
				:aria-label="`Zobrazit více o pobočce ${selectedHypermarket}`"
				@click="handleClick('Více o pobočce | Otevírací doba')"
			>
				<NuxtIcon
					name="custom/arrow-right"
					class="text-lg transition duration-300"
					filled
					aria-hidden="true"
				/>
				Více o pobočce
			</AbsoluteInternalLink>
		</template>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '../../../stores/storyblokHypermarkets';
	import {useHypermarketCapacityStore} from '../../../stores/hypermarketCapacity';
	import {getPhoneLink} from '@/utils';

	type Emits = {
		(e: 'update:click', text: string): void
	};

	const emit = defineEmits<Emits>();

	const {n} = useI18n();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const branchSpecificPath = useBranchSpecificPath();

	const hypermarketCapacityStore = useHypermarketCapacityStore();
	await hypermarketCapacityStore.fetchCapacity();
	const {capacity} = storeToRefs(hypermarketCapacityStore);

	const {data: hypermarketDetails, loading: pending} = useHypermarketDetails(selectedHypermarket);
	const selectedBranchLabelFresh = computed(() => selectedHypermarketInstance.value?.branchType === 'fresh');

	function handleClick(text: string) {
		emit('update:click', text);
	}

	watch(selectedHypermarketInstance, () => {
		hypermarketCapacityStore.fetchCapacity();
	});
</script>
